import { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";
import confetti from "canvas-confetti";
import megaphoneSvg from "@/assets/icons/notification/megaphone.svg";
import verifiedStarSvg from "@/assets/icons/navbar/verified-star.svg";
import type { MSShoutOut } from "@/hooks/useCreateShoutOut";
import { useShoutOutConnectors } from "@/hooks/useCMSData";
import { celebrationButtonSx } from "../styles";

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const staggered = (delayMs: number, isActive: boolean) =>
  isActive
    ? {
        opacity: 0,
        animation: `${fadeSlideUp} 0.5s ease forwards`,
        animationDelay: `${delayMs}ms`,
      }
    : { opacity: 1 };

type ShoutOutSectionProps = {
  shoutOut: MSShoutOut;
  isActive: boolean;
  onAdvance: () => void;
  isLast: boolean;
};

export const ShoutOutSection = ({
  shoutOut,
  isActive,
  onAdvance,
  isLast,
}: ShoutOutSectionProps) => {
  const { connectors } = useShoutOutConnectors();
  const connector = connectors.find((c) => c.id === shoutOut.connectorId);

  useEffect(() => {
    if (!isActive) return undefined;
    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.inset = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "9999";
    document.body.appendChild(canvas);
    const myConfetti = confetti.create(canvas, { resize: true });
    const t = setTimeout(() => {
      myConfetti({
        particleCount: 120,
        spread: 100,
        origin: { x: 0.33, y: 0.5 },
        disableForReducedMotion: true,
      });
      myConfetti({
        particleCount: 120,
        spread: 100,
        origin: { x: 0.67, y: 0.5 },
        disableForReducedMotion: true,
      });
    }, 700);
    return () => {
      clearTimeout(t);
      myConfetti.reset();
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };
  }, [isActive]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        textAlign: "center",
        px: 1,
        width: "100%",
        maxWidth: 480,
      }}
    >
      <Box sx={staggered(150, isActive)}>
        <Typography
          sx={{
            fontFamily: "'Proxima Nova', sans-serif",
            fontWeight: 700,
            fontSize: { xs: 26, sm: 32 },
            lineHeight: 1.25,
            color: "#fff",
          }}
        >
          You got a Shout out:
        </Typography>
        {connector && (
          <Typography
            sx={{
              fontFamily: "'Proxima Nova', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 26, sm: 32 },
              lineHeight: 1.25,
              color: "#fff",
            }}
          >
            {connector.name}!
          </Typography>
        )}
      </Box>
      <Box sx={staggered(300, isActive)}>
        <Box
          component="img"
          src={megaphoneSvg}
          alt=""
          sx={{ width: 168, height: 168 }}
        />
      </Box>
      <Box
        sx={{
          ...staggered(450, isActive),
          width: "100%",
          bgcolor: "rgba(255, 255, 255, 0.12)",
          borderRadius: "16px",
          p: "20px 24px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Proxima Nova', sans-serif",
            fontStyle: "italic",
            fontSize: 18,
            lineHeight: "28px",
            color: "#fff",
          }}
        >
          {shoutOut.message ? `"${shoutOut.message}"` : "No message provided."}
        </Typography>
        {shoutOut.createdByName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mt: 1.5,
            }}
          >
            <Box
              component="img"
              src={verifiedStarSvg}
              alt=""
              sx={{ width: 20, height: 20, flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontFamily: "'Proxima Nova', sans-serif",
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "22px",
                color: "rgba(255, 255, 255, 0.8)",
              }}
            >
              {shoutOut.createdByName}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ width: "100%", ...staggered(600, isActive) }}>
        <Button
          onClick={onAdvance}
          fullWidth
          variant="contained"
          color="primary"
          disableElevation
          sx={celebrationButtonSx}
        >
          {isLast ? "Add to My Profile" : "Next"}
        </Button>
      </Box>
    </Box>
  );
};
